import { Link } from 'react-router-dom';
import { COMPANY, LegalDoc, ToConfirm, type LegalSection } from '../components/LegalDoc';

/**
 * Terms of service.
 *
 * Written against what the product actually does — progressive pricing, a free
 * package, payment by reference rather than an automated gateway, export on
 * exit — so that nothing here promises a feature that does not exist or omits
 * one that does.
 *
 * The obligation worth reading twice is §5. A landlord recording a tenant is
 * the data controller for that tenant, which means the lawful basis for
 * holding an ID number is theirs to have, not ours to assume. Saying so in the
 * terms is what makes the split in the privacy policy real rather than
 * decorative.
 */

const sections: LegalSection[] = [
  {
    id: 'agreement',
    heading: 'Who this agreement is between',
    body: (
      <>
        <p>
          These terms are between you — the person or business using NyumbaSuite — and{' '}
          <ToConfirm>{COMPANY.legalName}</ToConfirm>,{' '}
          <ToConfirm>{COMPANY.registration}</ToConfirm>, of{' '}
          <ToConfirm>{COMPANY.address}</ToConfirm>.
        </p>
        <p>
          By creating an account you accept them. If you are accepting on behalf of a
          company, you confirm you are entitled to bind it.
        </p>
        <p>
          They should be read alongside our <Link to="/privacy">Privacy Policy</Link>,
          which forms part of this agreement.
        </p>
      </>
    ),
  },
  {
    id: 'service',
    heading: 'What the service is',
    body: (
      <>
        <p>
          NyumbaSuite is software for managing rental property: properties and units,
          tenants and tenancies, rent and arrears, receipts and invoices, maintenance
          and expenses, inspections, reports, and advertising vacant units.
        </p>
        <p>
          It is a record-keeping and administration tool.{' '}
          <strong>It is not a bank, an escrow service, a letting agent, an accountant or
          a legal adviser.</strong> It does not hold or move your money: where it records
          a payment, that payment happened elsewhere and you are telling the system
          about it.
        </p>
        <p>
          We add to the product over time. Features may change; we will not remove
          something you depend on without notice and a way to get your data out.
        </p>
      </>
    ),
  },
  {
    id: 'account',
    heading: 'Your account',
    body: (
      <>
        <ul>
          <li>Give accurate details when you register, and keep them current.</li>
          <li>
            Keep your password to yourself. Anything done with your credentials is
            treated as done by you, so add a user rather than sharing a login — the
            product supports several, with different permissions.
          </li>
          <li>Tell us promptly if you believe your account has been reached by someone else.</li>
          <li>You must be at least eighteen.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'pricing',
    heading: 'Plans, pricing and payment',
    body: (
      <>
        <p>
          Pricing is progressive: what you pay follows the number of units you manage,
          and the rate per unit falls as the portfolio grows. The current rates are
          published on the <Link to="/#pricing">pricing section</Link> of our homepage
          and are the rates that apply.
        </p>
        <ul>
          <li>
            <strong>The free package</strong> is genuinely free. It carries a unit limit,
            published with the plan, and no card is required.
          </li>
          <li>
            <strong>Paid plans</strong> are billed for the period you select. Payment is
            by reference — you pay by the method agreed and give us the reference, and
            we confirm it. There is no automatic charging of a card.
          </li>
          <li>
            <strong>If your portfolio grows past your plan</strong>, the product tells you
            and asks you to move up. It does not silently bill you more.
          </li>
          <li>
            <strong>Enterprise and custom terms</strong> are agreed in writing and those
            terms take precedence over this section.
          </li>
        </ul>
        <p>
          We may change prices. Existing customers will be told before a change affects
          them, and a change never applies to a period already paid for.
        </p>
        <p>
          If payment is not made when due, the product moves through a grace period with
          warnings before any restriction. Your data is not deleted for non-payment, and
          you can always export it.
        </p>
      </>
    ),
  },
  {
    id: 'your-obligations',
    heading: 'Your responsibilities — particularly about tenants',
    body: (
      <>
        <p>
          This section carries the most weight in the agreement, so it is worth reading
          properly.
        </p>
        <p>
          When you record information about a tenant, <strong>you are the data
          controller for it and we are your processor.</strong> That means:
        </p>
        <ul>
          <li>
            You must have a lawful basis for collecting what you collect. An ID number or
            an employer&rsquo;s details are personal data, and the fact that a field
            exists in our software is not a reason to fill it in.
          </li>
          <li>
            You must tell your tenants what you hold and why, as the Data Protection Act,
            2019 requires of a controller.
          </li>
          <li>
            You must answer their requests to see, correct or delete their data. We will
            help you and will never obstruct you, but the decision is yours.
          </li>
          <li>
            You must keep what you record accurate, and not use the product to harass
            anyone or to hold data you have no reason to hold.
          </li>
        </ul>
        <p>You also agree not to:</p>
        <ul>
          <li>use the service unlawfully, or to discriminate unlawfully against tenants;</li>
          <li>upload anything malicious, or attempt to reach another account&rsquo;s data;</li>
          <li>advertise a property you have no right to let, or misdescribe one;</li>
          <li>resell or white-label the service without our written agreement.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'listings',
    heading: 'Listings and public content',
    body: (
      <>
        <p>
          If you advertise a unit on Find Your Next Home, the listing and its photographs
          become publicly visible. You confirm you have the right to let the property and
          to publish the images, and that the description is accurate — including the
          rent and the deposit.
        </p>
        <p>
          We review listings before they go live and may decline or remove one that is
          inaccurate, misleading, unlawful, or not genuinely available. Enquiries a
          listing attracts are passed to you, and how you handle them is your
          responsibility.
        </p>
        <p>
          If you send us a story for the blog, you keep ownership of what you wrote and
          give us permission to edit and publish it. You can withdraw that permission
          before it is published, and ask us to take it down afterwards.
        </p>
      </>
    ),
  },
  {
    id: 'our-obligations',
    heading: 'What we undertake, and what we do not promise',
    body: (
      <>
        <p>We will:</p>
        <ul>
          <li>provide the service with reasonable skill and care;</li>
          <li>keep your account separated from every other account;</li>
          <li>not access your business records except where you ask us to, under a recorded, time-limited grant;</li>
          <li>give you a way to export everything you have entered, at any time.</li>
        </ul>
        <p>
          We do not promise that the service will be uninterrupted or free of faults.
          Maintenance happens, providers have outages, and connections fail. We aim for
          high availability and will tell you about planned interruptions where we can.
        </p>
        <p>
          <strong>Keep your own copies of anything you cannot afford to lose.</strong>{' '}
          The export tool exists for this, and using it periodically is sensible whatever
          any software vendor tells you about their backups.
        </p>
      </>
    ),
  },
  {
    id: 'ownership',
    heading: 'Who owns what',
    body: (
      <>
        <p>
          <strong>Your data stays yours.</strong> Everything you enter — your properties,
          your tenants, your records — belongs to you. We claim no ownership of it and we
          do not use it for our own purposes.
        </p>
        <p>
          The software, its design, its name and its content belong to us. Using the
          service does not transfer any of that to you.
        </p>
      </>
    ),
  },
  {
    id: 'ending',
    heading: 'Ending the agreement',
    body: (
      <>
        <p>
          You can stop using NyumbaSuite whenever you like. Closing your account ends
          this agreement.
        </p>
        <p>
          We may suspend or close an account that breaches these terms, is being used
          unlawfully, or endangers the service or other customers. Except where the
          breach is serious or unlawful, we will tell you first and give you a chance to
          put it right.
        </p>
        <p>
          <strong>Before you go, export your data</strong> from{' '}
          <Link to="/data">Import &amp; export</Link>. After closure we begin removing it
          as described in the <Link to="/privacy#how-long">Privacy Policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    heading: 'Liability',
    body: (
      <>
        <p>
          Nothing here limits liability that cannot lawfully be limited — including for
          death or personal injury caused by negligence, or for fraud.
        </p>
        <p>
          Beyond that, and to the extent the law allows: we are not liable for lost
          profits, lost business, or indirect or consequential loss; and our total
          liability arising from the service in any twelve-month period is limited to
          the amount you paid us in that period.
        </p>
        <p>
          We are not liable for decisions you take on the basis of the records or reports
          the product produces. It reports what you told it. Whether a deposit should be
          returned, whether a tenancy should end, whether damage is fair wear — those are
          yours to decide, and the product is deliberately built not to decide them for
          you.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to these terms',
    body: (
      <p>
        We may update these terms. The effective date at the top will change, and we
        will tell account holders directly about any change that materially affects
        them. Continuing to use the service after a change means you accept it; if you
        do not, you may close your account and export your data.
      </p>
    ),
  },
  {
    id: 'law',
    heading: 'Governing law',
    body: (
      <>
        <p>
          This agreement is governed by the laws of Kenya, and the courts of Kenya have
          jurisdiction over any dispute arising from it.
        </p>
        <p>
          Before going to court, please raise the matter with us at{' '}
          <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. Most disagreements are
          faster to settle in an email than anywhere else.
        </p>
      </>
    ),
  },
];

export function Terms() {
  return (
    <LegalDoc
      title="Terms of Service"
      lede="The agreement between you and NyumbaSuite: what the service does, what it costs, and what each of us is responsible for."
      effectiveDate="21 August 2026"
      canonicalPath="/terms"
      draft
      sections={sections}
    />
  );
}
